import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { Loader2 } from 'lucide-react';
import { buttonVariants, gapClasses, getTextClassName } from './button-variants';
import { cn } from './utils';

import type { VariantProps } from 'class-variance-authority';
import type { LinkProps } from 'react-router';

// Polymorphic element types for the `as` prop
export type ButtonElementType = 'button' | 'a' | 'div' | React.ComponentType<LinkProps>;

type ElementTypeMap = {
  button: HTMLButtonElement;
  a: HTMLAnchorElement;
  div: HTMLDivElement;
};

type GetElementType<T extends ButtonElementType> = T extends keyof ElementTypeMap ? ElementTypeMap[T] : HTMLElement;

type ButtonState = 'default' | 'loading';
type GapSize = 1 | 2 | 3 | 4;

type ButtonBaseProps = {
  asChild?: boolean;
  as?: ButtonElementType;
  state?: ButtonState;
  isLoading?: boolean;
  loadingPlaceholder?: React.ReactNode;
  leadIcon?: React.ReactNode;
  trailIcon?: React.ReactNode;
  gap?: GapSize;
  textClassName?: string;
  textContainerClassName?: string;
  hasBackgroundBlur?: boolean;
  keepPointerEvents?: boolean;
  disabled?: boolean;
} & VariantProps<typeof buttonVariants>;

export type ButtonProps<T extends ButtonElementType = 'button'> = ButtonBaseProps &
  Omit<
    T extends 'button'
      ? React.ButtonHTMLAttributes<HTMLButtonElement>
      : T extends 'a'
        ? React.AnchorHTMLAttributes<HTMLAnchorElement>
        : T extends 'div'
          ? React.HTMLAttributes<HTMLDivElement>
          : React.ComponentProps<T>,
    keyof ButtonBaseProps
  >;

// Generic forwardRef wrapper for polymorphic component
const Button = React.forwardRef(
  <T extends ButtonElementType = 'button'>(
    {
      className,
      variant = 'default',
      size = 'default',
      as,
      asChild = false,
      state = 'default',
      isLoading: isLoadingProp,
      loadingPlaceholder,
      leadIcon,
      trailIcon,
      gap = 2,
      disabled,
      children,
      textClassName,
      textContainerClassName,
      hasBackgroundBlur = true, // Changed default to true
      keepPointerEvents = false,
      onClick,
      onTouchStart,
      onTouchEnd,
      ...props
    }: ButtonProps<T>,
    ref: React.ForwardedRef<GetElementType<T>>,
  ) => {
    // Determine component: asChild uses Slot, otherwise use `as` prop or default to 'button'
    const Comp = asChild ? Slot : (as ?? 'button');

    // Support both `isLoading` prop and `state="loading"`
    const isLoading = !!isLoadingProp || state === 'loading';
    const isDisabled = !!disabled || isLoading;
    const isLinkVariant = variant === 'link' || variant === 'link-secondary';

    const textColor = getTextClassName(variant, size, isDisabled);

    // Handle click with disabled check (for non-button elements that don't respect disabled)
    const handleClick = onClick
      ? (e: React.MouseEvent) => {
          if (isDisabled) return;
          (onClick as (e: React.MouseEvent) => void)(e);
        }
      : undefined;

    const handleTouchStart = onTouchStart
      ? (e: React.TouchEvent) => {
          if (isDisabled) return;
          (onTouchStart as (e: React.TouchEvent) => void)(e);
        }
      : undefined;

    const handleTouchEnd = onTouchEnd
      ? (e: React.TouchEvent) => {
          if (isDisabled) return;
          (onTouchEnd as (e: React.TouchEvent) => void)(e);
        }
      : undefined;

    // Build common props object
    const commonProps = {
      className: cn(
        buttonVariants({ variant, size }),
        isLinkVariant && 'px-0 py-0', // Override padding for links
        isDisabled && !keepPointerEvents && 'pointer-events-none',
        hasBackgroundBlur && 'hover:backdrop-blur-md',
        as === 'a' && 'cursor-pointer',
        className,
      ),
      ref,
      disabled: isDisabled,
      'aria-busy': isLoading,
      onClick: handleClick,
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchEnd,
      ...props,
    };

    const content = (
      <span className={cn('flex items-center', gapClasses[gap], textContainerClassName)}>
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-text-disabled" />
        ) : (
          leadIcon && <span className={cn('flex-shrink-0', textColor, textClassName)}>{leadIcon}</span>
        )}
        <span className={cn('flex items-center shrink-0 text-nowrap', textColor, !isLinkVariant && 'font-medium', textClassName)}>
          {isLoading && loadingPlaceholder ? loadingPlaceholder : children}
        </span>
        {/* trailIcon stays visible during loading */}
        {trailIcon && <span className={cn('flex-shrink-0', textColor, textClassName)}>{trailIcon}</span>}
      </span>
    );

    // Use createElement to avoid complex type intersections
    return React.createElement(Comp as React.ElementType, commonProps, content);
  },
) as <T extends ButtonElementType = 'button'>(
  props: ButtonProps<T> & { ref?: React.ForwardedRef<GetElementType<T>> },
) => React.ReactElement;

(Button as React.FC).displayName = 'Button';

export { Button, buttonVariants };
